import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { useDebounce } from '@/hooks/useDebounce';
import type { Conversation } from '@/types';

interface PersonSearchHit {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  whatsappNumber: string | null;
}

interface CompanySearchHit {
  id: string;
  name: string;
  phone: string | null;
}

type CustomerOption =
  | {
      kind: 'person';
      id: string;
      name: string;
      phone: string;
      whatsappNumber: string;
    }
  | {
      kind: 'company';
      id: string;
      name: string;
      phone: string;
    };

interface PaginatedResponse<T> {
  data: T[];
}

interface NewConversationDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (conversation: Conversation) => void;
}

function optionNumber(option: CustomerOption): string {
  if (option.kind === 'person') {
    return option.whatsappNumber || option.phone;
  }
  return option.phone;
}

export default function NewConversationDialog({
  open,
  onClose,
  onCreated,
}: NewConversationDialogProps) {
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CustomerOption | null>(null);
  const [phone, setPhone] = useState('');
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelected(null);
      setPhone('');
      setOptions([]);
      setLoading(false);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const query = debouncedSearch.trim();
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void Promise.all([
      api.get<PaginatedResponse<PersonSearchHit>>('/persons', {
        params: { q: query, limit: 10 },
      }),
      api.get<PaginatedResponse<CompanySearchHit>>('/companies', {
        params: { q: query, limit: 10 },
      }),
    ])
      .then(([persons, companies]) => {
        if (cancelled) return;
        const personOptions: CustomerOption[] = (persons.data ?? []).map((person) => ({
          kind: 'person',
          id: person.id,
          name: `${person.firstName} ${person.lastName}`.trim(),
          phone: person.phone ?? '',
          whatsappNumber: person.whatsappNumber ?? '',
        }));
        const companyOptions: CustomerOption[] = (companies.data ?? []).map((company) => ({
          kind: 'company',
          id: company.id,
          name: company.name,
          phone: company.phone ?? '',
        }));
        setOptions([...personOptions, ...companyOptions]);
      })
      .catch(() => {
        if (cancelled) return;
        setOptions([]);
        notify.error(tc('error'), t('conversationsLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // Including t/tc retriggers the search on every render of the i18n test mock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, open, notify]);

  const crmHasNumber = selected ? optionNumber(selected).trim() !== '' : true;
  const canCreate = Boolean(selected && phone.trim() && !submitting);

  const handleSelect = (option: CustomerOption) => {
    setSelected(option);
    setPhone(optionNumber(option));
  };

  const personOptions = options.filter((option) => option.kind === 'person');
  const companyOptions = options.filter((option) => option.kind === 'company');

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!selected || !phone.trim()) return;
    setSubmitting(true);
    try {
      const conversation = await api.post<Conversation>('/conversations', {
        participantType: 'customer',
        participantId: selected.id,
        waPhoneNumber: phone.trim(),
      });
      notify.success(t('newConversationCreated'));
      onCreated(conversation);
      onClose();
    } catch {
      notify.error(t('newConversationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      mobileTitle={t('newConversation')}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogHeader title={t('newConversation')} onClose={handleClose} closeDisabled={submitting} />
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('newConversationCustomer')}
            helperText={t('newConversationCustomerHint')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />
          {search.trim().length >= 2 && (
            <List dense disablePadding>
              {personOptions.length > 0 && (
                <ListSubheader disableSticky>{t('newConversationPersons')}</ListSubheader>
              )}
              {personOptions.map((option) => {
                const number = optionNumber(option);
                return (
                  <ListItemButton
                    key={`${option.kind}-${option.id}`}
                    selected={selected?.kind === option.kind && selected.id === option.id}
                    onClick={() => handleSelect(option)}
                  >
                    <ListItemText primary={number ? `${option.name} · ${number}` : option.name} />
                  </ListItemButton>
                );
              })}
              {companyOptions.length > 0 && (
                <ListSubheader disableSticky>{t('newConversationCompanies')}</ListSubheader>
              )}
              {companyOptions.map((option) => {
                const number = optionNumber(option);
                return (
                  <ListItemButton
                    key={`${option.kind}-${option.id}`}
                    selected={selected?.kind === option.kind && selected.id === option.id}
                    onClick={() => handleSelect(option)}
                  >
                    <ListItemText primary={number ? `${option.name} · ${number}` : option.name} />
                  </ListItemButton>
                );
              })}
              {!loading && options.length === 0 ? (
                <ListItemButton disabled>
                  <ListItemText primary={tc('noResults')} />
                </ListItemButton>
              ) : null}
            </List>
          )}
          <TextField
            label={t('newConversationPhone')}
            helperText={t('newConversationPhoneHint')}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            fullWidth
            disabled={!selected}
          />
          {selected && !crmHasNumber && !phone.trim() ? (
            <Alert severity="warning">{t('newConversationNoPhone')}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => { void handleSubmit(); }} disabled={!canCreate}>
          {submitting ? tc('saving') : t('newConversationCreate')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
